import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Button, Stack, Typography } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { getTeamMemberDisplayName } from '../../../../utils/nameUtils';
import CheckboxWithNotes from 'src/components/CheckboxWithNotes';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import { AdlNames } from 'src/constants/CareInstructions';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import type { AdlDetails, CareInstructions } from 'src/store/sageHomeApi';
import { useUpdateCareInstructionsMutation } from 'src/store/sageHomeApi';
import { selectBearerToken } from 'src/store/slices/authSlice';
import { selectAdls, selectCareInstructions } from 'src/store/slices/careInstructionsSlice';

export const Route = createFileRoute('/(app)/care/adls/edit')({
    component: AdlsEditComponent,
});
interface AdlFormData {
    adls: AdlDetails[];
}

function AdlsEditComponent() {
    const navigate = useNavigate();
    const bearerToken = useSelector(selectBearerToken);
    const adls = useSelector(selectAdls);
    const { careRecipient } = useTeamMemberData();
    const [selectedAdlName, setSelectedAdlName] = useState<string | null>(null);
    const { showSuccess, showError } = useSnackbar();
    const careInstructions = useSelector(selectCareInstructions);

    const [updateCareInstructions] = useUpdateCareInstructionsMutation();

    const { control, handleSubmit, watch, setValue, formState } = useForm<AdlFormData>({
        defaultValues: { adls: adls },
    });

    const { fields } = useFieldArray({ control, name: 'adls' });

    const watchedAdls = watch('adls');

    const onSubmit = async (data: AdlFormData) => {
        if (!careRecipient?.teamMemberRid) return;

        try {
            // Only include enabled ADLs in the API call
            const enabledAdls = data.adls.filter((adl) => adl.enabled);
            const careInstructionsToSubmit: CareInstructions = {
                adls: enabledAdls,
            };

            // Update existing care instructions
            await updateCareInstructions({
                authorization: bearerToken || '',
                careRecipientRid: careRecipient.teamMemberRid,
                careInstructionsRequest: careInstructionsToSubmit,
            }).unwrap();

            showSuccess('Care needs updated');

            navigate({ to: '/care/day-to-day/intro' });
        } catch (error) {
            console.error('Failed to save care instructions:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleCheckboxChange = (index: number, checked: boolean) => {
        const updatedAdl = { ...watchedAdls[index], enabled: checked };

        setValue(`adls.${index}`, updatedAdl, { shouldDirty: true });

        if (!checked) {
            // Clear notes when unchecked
            const clearedAdl = { ...updatedAdl, notes: '' };
            setValue(`adls.${index}`, clearedAdl, { shouldDirty: true });
        }
    };

    const handleNotesChange = (index: number, notes: string) => {
        const updatedAdl = { ...watchedAdls[index], notes };
        setValue(`adls.${index}`, updatedAdl, { shouldDirty: true });
    };

    const handleSaveNotes = () => {
        setSelectedAdlName(null);
    };

    const handleCancelNotes = (index: number) => {
        // Reset notes to original value
        const originalAdl = careInstructions?.adls?.[index];
        setValue(`adls.${index}.notes`, originalAdl?.notes || '', { shouldDirty: true });
        setSelectedAdlName(null);
    };

    return (
        <InnerMaxWidthContainer>
            <PageHeader
                backButtonProps={{
                    shouldConfirmAction: formState.isDirty,
                    confirmationDialogProps: {
                        title: 'Are you sure you want to discard the changes?',
                        confirmText: 'Discard Changes',
                        cancelText: 'Go Back',
                    },
                }}
            />
            <Stack sx={{ gap: 4 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        What does{' '}
                        {getTeamMemberDisplayName(careRecipient, {
                            firstOnly: true,
                            usePreferred: true,
                        })}{' '}
                        need help with?
                    </Typography>
                    <Typography variant="body1">
                        Please select from the activities below. You can add notes to your
                        selections.
                    </Typography>
                </Stack>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack sx={{ gap: 3, mb: 4 }}>
                        {fields.map((field, index) => {
                            const adl = watchedAdls[index];
                            const isEditing = selectedAdlName === adl.name;

                            return (
                                <CheckboxWithNotes
                                    key={field.id}
                                    label={AdlNames[adl.name as string]}
                                    iconName={adl.name}
                                    checked={adl.enabled || false}
                                    notes={adl.notes || ''}
                                    onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                                    onNotesChange={(notes) => handleNotesChange(index, notes)}
                                    onSaveNotes={handleSaveNotes}
                                    onCancelNotes={() => handleCancelNotes(index)}
                                    isEditing={isEditing}
                                    onStartEditing={() => setSelectedAdlName(adl.name || '')}
                                />
                            );
                        })}
                    </Stack>
                </form>
            </Stack>

            <PageFooter includeShadow>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit(onSubmit)}
                    disabled={formState.isSubmitting}
                >
                    {formState.isSubmitting ? 'Saving...' : 'Save & Continue'}
                </Button>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
